import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, fail } from "@/lib/api";

const AuthCtx = createContext(null);
const waitForAuthRetry = (delay, signal) => new Promise(resolve => {
  const done = () => { clearTimeout(timer); signal.removeEventListener("abort", done); resolve(); };
  const timer = setTimeout(done, delay);
  signal.addEventListener("abort", done, { once: true });
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const epoch = useRef(0);
  const pending = useRef(null);
  const cancelPending = useCallback(() => { ++epoch.current; pending.current?.abort(); }, []);
  const refresh = useCallback(async () => {
    cancelPending();
    const version = epoch.current;
    const controller = new AbortController(); pending.current = controller;
    setRefreshing(true); setAuthError(false);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const { data } = await api.get("/auth/me", { signal: controller.signal });
          if (version !== epoch.current || controller.signal.aborted) return;
          setUser(data);
          return data;
        } catch (error) {
          if (version !== epoch.current || controller.signal.aborted || error?.code === "ERR_CANCELED") return;
          if ([401, 403].includes(error?.response?.status)) { setUser(false); return; }
          const transient = !error?.response || error.response.status >= 500;
          if (!transient || attempt === 2) { setAuthError(true); return; }
          await waitForAuthRetry(attempt === 0 ? 800 : 1800, controller.signal);
          if (version !== epoch.current || controller.signal.aborted) return;
        }
      }
    } finally {
      if (version === epoch.current && !controller.signal.aborted) setRefreshing(false);
    }
  }, [cancelPending]);
  useEffect(() => {
    refresh();
    const out = () => { cancelPending(); setRefreshing(false); setAuthError(false); setUser(false); };
    window.addEventListener("lbc:logout", out);
    return () => { cancelPending(); window.removeEventListener("lbc:logout", out); };
  }, [refresh, cancelPending]);
  const logout = async () => {
    cancelPending(); setRefreshing(false);
    try {
      await api.post("/auth/logout");
      cancelPending(); setRefreshing(false);
      setUser(false); setAuthError(false);
      return true;
    } catch (error) { fail(error); return false; }
  };
  return <AuthCtx.Provider value={{ user, setUser, refresh, logout, authError, refreshing }}>
    {authError && <div role="alert" className="border-b border-[#FFBD36]/40 bg-[#141C21] p-4 text-sm" data-testid="auth-unavailable">
      <p>La connexion au serveur n’a pas pu être vérifiée. Réessaie sans ressaisir ton mot de passe.</p>
      <button type="button" className="mt-2 underline" disabled={refreshing} onClick={refresh}>Réessayer la connexion</button>
    </div>}
    {children}
  </AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
